/**
 * Paths to JSON Converter for Insights
 *
 * Converts arrays of insight paths to filtered InsightsAnalysis objects.
 * Used for preparing hydrated data for AI server submission.
 *
 * Handles 3 granularity levels:
 * - @insight[id] → include whole analysis
 * - @insight[id].category → include all insights in category
 * - @insight[id].category[itemId] → include single insight
 */

import type { InsightsHydratedData, InsightsAnalysisForPicker, AggregatedInsightItem, JtbdCategory } from './types';
import { JTBD_CATEGORIES } from './types';

/**
 * Parsed insight path structure
 */
interface ParsedInsightPath {
  analysisId: string;
  category?: string;
  insightId?: string;
  level: 'analysis' | 'category' | 'item';
}

/**
 * Parses an insight path into structured components
 */
function parseInsightPath(path: string): ParsedInsightPath | null {
  // Item level: @insight[id].category[itemId]
  // The category class excludes `.` (W13 DM1, AC-W13-33): a loose `[^[]+` would
  // read `@insight[a].fears.sources[item-9]` as category `fears.sources` and
  // promote the quote's item key to a phantom thesis id.
  const itemMatch = path.match(/^@insight\[([^\]]+)\]\.([^[\].]+)\[([^\]]+)\]$/);
  if (itemMatch) {
    return { analysisId: itemMatch[1], category: itemMatch[2], insightId: itemMatch[3], level: 'item' };
  }

  // Category level: @insight[id].category
  const catMatch = path.match(/^@insight\[([^\]]+)\]\.([^[.]+)$/);
  if (catMatch) {
    return { analysisId: catMatch[1], category: catMatch[2], level: 'category' };
  }

  // Analysis level: @insight[id]
  const analysisMatch = path.match(/^@insight\[([^\]]+)\]$/);
  if (analysisMatch) {
    return { analysisId: analysisMatch[1], level: 'analysis' };
  }

  return null;
}

/**
 * Converts insight paths to hydrated analysis objects.
 *
 * For each selected analysis, returns only the selected categories/items
 * based on path granularity.
 */
export function pathsToJson(
  paths: string[],
  allAnalyses: InsightsAnalysisForPicker[],
): InsightsHydratedData {
  // Parse all paths
  const parsed = paths.map(parseInsightPath).filter((p): p is ParsedInsightPath => p !== null);

  // Group by analysisId
  const byAnalysis = new Map<string, ParsedInsightPath[]>();
  for (const p of parsed) {
    if (!byAnalysis.has(p.analysisId)) {
      byAnalysis.set(p.analysisId, []);
    }
    byAnalysis.get(p.analysisId)!.push(p);
  }

  const analyses: InsightsHydratedData['analyses'] = [];

  for (const [analysisId, analysisPaths] of byAnalysis) {
    const analysis = allAnalyses.find((a) => a.id === analysisId);
    if (!analysis || !analysis.aggregatedInsights) continue;

    // Check if whole analysis is selected
    const hasWholeAnalysis = analysisPaths.some((p) => p.level === 'analysis');

    const filteredInsights: Record<string, AggregatedInsightItem[]> = {};

    if (hasWholeAnalysis) {
      // Include all categories and items
      for (const cat of JTBD_CATEGORIES) {
        const items = analysis.aggregatedInsights[cat];
        if (Array.isArray(items) && items.length > 0) {
          filteredInsights[cat] = items;
        }
      }
    } else {
      // Filter by category and item paths
      const wholeCats = new Set(
        analysisPaths.filter((p) => p.level === 'category').map((p) => p.category!),
      );
      const itemPaths = analysisPaths.filter((p) => p.level === 'item');

      for (const cat of JTBD_CATEGORIES) {
        const items = analysis.aggregatedInsights[cat];
        if (!Array.isArray(items) || items.length === 0) continue;

        if (wholeCats.has(cat)) {
          // Whole category selected
          filteredInsights[cat] = items;
        } else {
          // Filter to selected items only
          const selectedIds = new Set(
            itemPaths.filter((p) => p.category === cat).map((p) => p.insightId!),
          );
          if (selectedIds.size > 0) {
            filteredInsights[cat] = items.filter((item) => selectedIds.has(item.id));
          }
        }
      }
    }

    if (Object.keys(filteredInsights).length > 0) {
      analyses.push({
        id: analysisId,
        name: analysis.name,
        summary: analysis.summary,
        mode: analysis.mode,
        aggregatedInsights: filteredInsights,
        audienceBuyingMechanics: analysis.audienceBuyingMechanics,
        audienceContentBriefs: analysis.audienceContentBriefs,
        audienceReadinessProfiles: analysis.audienceReadinessProfiles,
        mixedModeWarning: analysis.mixedModeWarning,
      });
    }
  }

  return { analyses };
}
